import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { LogOut, Settings, Award, TrendingUp, Eye, Heart, Users, Coins, Diamond, Zap, ChevronRight, BadgeCheck, Edit3, Instagram, Youtube, Share2, Trophy, Radio, Flag, ShieldOff, MoreVertical, BarChart3, UserPlus, UserCheck, MessageCircle, X, Play, Wand2, RefreshCw, Star, Copy } from "lucide-react";
import { AvatarFrame, UserName } from "../lib/cosmetics";
import ShareSheet from "../components/ShareSheet";
import ProfileReelPlayer from "../components/ProfileReelPlayer";
import ProfileActionsMenu from "../components/ProfileActionsMenu";
import { showGoldlyReward } from "../components/GoldlyReward";

const CRITERIA = [
  { label: "1,000+ Followers", icon: Users },
  { label: "10,000+ Total Views", icon: Eye },
  { label: "30+ Days Account Age", icon: BadgeCheck },
  { label: "5+ Reels Uploaded/Month", icon: TrendingUp },
  { label: "No Community Violations", icon: Award },
];

function BadgePill({ badge, level }) {
  const map = {
    rookie: { name: "Rookie", emoji: "🎯", color: "#94A3B8" },
    bronze: { name: "Bronze", emoji: "🥉", color: "#CD7F32" },
    silver: { name: "Silver", emoji: "🥈", color: "#C0C0C0" },
    gold: { name: "Gold", emoji: "🥇", color: "#FFD700" },
    diamond: { name: "Diamond", emoji: "💎", color: "#00E5FF" },
    legend: { name: "Legend", emoji: "👑", color: "#FF2A5F" },
  };
  const b = map[badge] || map.rookie;
  return (
    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border" style={{borderColor: b.color, color: b.color, background: b.color + "15"}}>
      <span>{b.emoji}</span>
      <span>{b.name}</span>
      <span className="text-white/50">• Lv {level}</span>
    </div>
  );
}

export default function Profile() {
  const { userId } = useParams();
  const { user: me, logout, refresh } = useAuth();
  const [user, setUser] = useState(null);
  const [reels, setReels] = useState([]);
  const [topGifters, setTopGifters] = useState([]);
  const [showShare, setShowShare] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [actionMsg, setActionMsg] = useState("");
  const [isFollowing, setIsFollowing] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);
  const [playingReelIndex, setPlayingReelIndex] = useState(null);
  const [refreshingAcc, setRefreshingAcc] = useState(false);

  const isOwn = !userId || userId === me?.id;

  const reload = () => {
    const id = userId || me?.id;
    if (!id) return;
    api.get(`/users/${id}`).then(r => {
      setUser(r.data);
      setReels(r.data.reels || []);
      setIsFollowing(!!r.data.is_following);
    });
    api.get(`/users/${id}/top-gifters`).then(r => setTopGifters(r.data)).catch(()=>{});
  };

  const refreshAccount = async () => {
    setRefreshingAcc(true);
    try {
      await refresh?.();
      reload();
      setActionMsg("✓ Account refreshed");
      setTimeout(() => setActionMsg(""), 1500);
    } catch { /* noop */ }
    finally { setRefreshingAcc(false); }
  };

  useEffect(() => {
    const id = userId || me?.id;
    if (!id) return;
    api.get(`/users/${id}`).then(r => {
      setUser(r.data);
      setReels(r.data.reels || []);
      setIsFollowing(!!r.data.is_following);
      setIsBlocked(!!r.data.is_blocked);
    });
    api.get(`/users/${id}/top-gifters`).then(r => setTopGifters(r.data)).catch(()=>{});
    // Auto-credit Goldly for viewing someone else's profile
    if (!isOwn && id && me?.id) {
      api.post(`/users/${id}/view-profile`).then(r => {
        if (r.data?.earned > 0) showGoldlyReward(r.data.earned, "view_profile");
      }).catch(() => {});
    }
  }, [userId, me?.id]);

  const toggleFollow = async () => {
    if (followBusy || !user) return;
    setFollowBusy(true);
    try {
      const { data } = await api.post(`/users/${user.id}/follow`);
      setIsFollowing(!!data.following);
      setUser(u => u ? { ...u, followers: (u.followers || 0) + (data.following ? 1 : -1) } : u);
      if (data.earned > 0) showGoldlyReward(data.earned, "follow");
    } catch {}
    finally { setFollowBusy(false); }
  };

  if (!user) return <div className="p-6 text-white/60">Loading...</div>;

  const flash = (m) => { setActionMsg(m); setTimeout(()=>setActionMsg(""), 2500); };
  const onBlockChange = (blocked, m) => {
    setIsBlocked(blocked);
    if (blocked) { setIsFollowing(false); setReels([]); } else { reload(); }
    flash(m);
  };
  const hidden = !isOwn && (isBlocked || user.blocked_me);

  return (
    <div className="px-4 py-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-outfit font-extrabold text-3xl tracking-tighter truncate min-w-0 flex-1 mr-2">{isOwn ? "Profile" : "@" + (user.username || user.name || "user")}</h1>
        <div className="flex gap-2 relative flex-shrink-0">
          <button onClick={()=>setShowShare(true)} data-testid="profile-share" className="p-2 rounded-full bg-white/5 border border-white/10"><Share2 className="w-4 h-4"/></button>
          {isOwn ? (
            <>
              <button onClick={refreshAccount} disabled={refreshingAcc} data-testid="profile-refresh"
                className="p-2 rounded-full bg-white/5 border border-white/10 active:scale-95 disabled:opacity-50" title="Refresh account & wallet">
                <RefreshCw className={`w-4 h-4 ${refreshingAcc ? "animate-spin text-[#00E5FF]" : ""}`}/>
              </button>
              <Link to="/dashboard" data-testid="goto-dashboard" className="px-3 py-1.5 rounded-full bg-[#00E5FF]/15 border border-[#00E5FF]/40 text-[#00E5FF] text-xs font-bold flex items-center gap-1">
                <BarChart3 className="w-3.5 h-3.5"/>Insights
              </Link>
              <Link to="/settings" data-testid="goto-settings" className="p-2 rounded-full bg-white/5 border border-white/10"><Settings className="w-4 h-4"/></Link>
              <Link to="/profile/edit" data-testid="profile-edit" className="p-2 rounded-full bg-white/5 border border-white/10"><Edit3 className="w-4 h-4"/></Link>
              {me?.role === "admin" && (
                <Link to="/admin" data-testid="goto-admin" className="px-3 py-1.5 rounded-full bg-[#FF7A00]/15 border border-[#FF7A00]/40 text-[#FF7A00] text-xs font-bold flex items-center gap-1">
                  <Award className="w-3.5 h-3.5"/>Admin
                </Link>
              )}
              <button onClick={logout} data-testid="logout-btn" className="p-2 rounded-full bg-white/5 border border-white/10"><LogOut className="w-4 h-4"/></button>
            </>
          ) : (
            <ProfileActionsMenu user={user} isBlocked={isBlocked} onBlockChange={onBlockChange} onMessage={flash}/>
          )}
        </div>
      </div>

      {actionMsg && <div className="mb-3 p-2 rounded-xl bg-emerald-500/10 text-emerald-300 text-xs text-center" data-testid="action-msg">{actionMsg}</div>}

      <div className="flex items-center gap-4 mb-3">
        <AvatarFrame userId={user.id} src={user.avatar} size={80}/>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <div className="font-outfit font-bold text-lg truncate"><UserName userId={user.id}>{user.name}</UserName></div>
            {(user.email_verified || user.email_verified_at) && <Star className="w-4 h-4 text-[#FFD700] fill-[#FFD700] flex-shrink-0" data-testid="email-verified-star" aria-label="Email verified"/>}
            {user.verified && <BadgeCheck className="w-5 h-5 text-[#00E5FF] flex-shrink-0" data-testid="verified-tick"/>}
          </div>
          <div className="text-sm text-white/60">@{user.username || user.name || "user"}</div>
          <div className="mt-1.5 flex items-center gap-2 flex-wrap">
            <BadgePill badge={user.stream_badge || "rookie"} level={user.stream_level || 1}/>
            {user.hifi_id && (
              <button onClick={() => { navigator.clipboard?.writeText(user.hifi_id); flash("Hifi ID copied"); }} data-testid="profile-hifi-id"
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#FFD700]/10 border border-[#FFD700]/40 text-[11px] font-bold active:scale-95">
                <span className="text-[#FFD700]">HIFI ID</span><span className="font-mono tracking-wider">{user.hifi_id}</span><Copy className="w-3 h-3 text-white/50"/>
              </button>
            )}
          </div>
        </div>
      </div>

      {!isOwn && isBlocked && (
        <div className="mb-3 p-3 rounded-2xl bg-[#FF2A5F]/10 border border-[#FF2A5F]/30 flex items-center justify-between gap-2" data-testid="blocked-banner">
          <span className="text-sm text-white/80">You blocked this user</span>
          <button onClick={async()=>{ await api.delete(`/users/${user.id}/block`); onBlockChange(false, "User unblocked"); }} data-testid="banner-unblock"
            className="px-4 py-1.5 rounded-full bg-white text-black text-xs font-extrabold active:scale-95">Unblock</button>
        </div>
      )}
      {!isOwn && !isBlocked && user.blocked_me && (
        <div className="mb-3 p-3 rounded-2xl bg-white/5 border border-white/10 text-sm text-white/70" data-testid="blocked-me-banner">This account isn't available to you.</div>
      )}
      {!isOwn && !hidden && (
        <div className="flex gap-2 mb-3" data-testid="profile-follow-row">
          <button onClick={toggleFollow} disabled={followBusy} data-testid={isFollowing ? "btn-unfollow" : "btn-follow"}
            className={`flex-1 py-2 rounded-full text-sm font-extrabold flex items-center justify-center gap-1 active:scale-95 disabled:opacity-50 ${
              isFollowing ? "bg-white/10 border border-white/20 text-white" : "bg-[#00E5FF] text-black neon-cyan"
            }`}>
            {isFollowing ? <><UserCheck className="w-4 h-4"/>Following</> : <><UserPlus className="w-4 h-4"/>Follow</>}
          </button>
          <Link to={`/dm/${user.id}`} data-testid="btn-message"
            className="flex-1 py-2 rounded-full bg-white/10 border border-white/20 text-white text-sm font-bold flex items-center justify-center gap-1 active:scale-95">
            <MessageCircle className="w-4 h-4"/>Message
          </Link>
        </div>
      )}

      {user.bio && <p className="text-sm text-white/80 mb-3 leading-relaxed">{user.bio}</p>}

      {/* Social links */}
      {(user.instagram_url || user.youtube_url) && (
        (() => {
          // SEC-004 client sanitiser — never anchor to non-http(s) schemes even
          // if the DB was populated before the backend validator existed.
          const safe = (u) => {
            if (!u) return "";
            const s = String(u).trim();
            const low = s.toLowerCase();
            if (low.startsWith("https://") || low.startsWith("http://")) return s;
            return ""; // block javascript:, data:, etc.
          };
          const ig = safe(user.instagram_url);
          const yt = safe(user.youtube_url);
          if (!ig && !yt) return null;
          return (
        <div className="flex items-center gap-2 mb-4" data-testid="social-links">
          {ig && (
            <a href={ig} target="_blank" rel="noopener noreferrer" data-testid="link-instagram"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#E4405F]/15 border border-[#E4405F]/30 text-[#E4405F] text-xs font-semibold">
              <Instagram className="w-3.5 h-3.5"/>Instagram
            </a>
          )}
          {yt && (
            <a href={yt} target="_blank" rel="noopener noreferrer" data-testid="link-youtube"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#FF0000]/15 border border-[#FF0000]/30 text-[#FF0000] text-xs font-semibold">
              <Youtube className="w-3.5 h-3.5"/>YouTube
            </a>
          )}
        </div>
      );
        })()
      )}

      <div className="grid grid-cols-3 gap-2 mb-5">
        <div className="text-center p-3 rounded-2xl bg-white/5 border border-white/10" data-testid="stat-followers">
          <div className="font-outfit font-extrabold text-xl">{user.followers ?? 0}</div>
          <div className="text-[10px] text-white/60 uppercase tracking-wide">Followers</div>
        </div>
        <div className="text-center p-3 rounded-2xl bg-white/5 border border-white/10">
          <div className="font-outfit font-extrabold text-xl">{user.following ?? 0}</div>
          <div className="text-[10px] text-white/60 uppercase tracking-wide">Following</div>
        </div>
        <div className="text-center p-3 rounded-2xl bg-white/5 border border-white/10">
          <div className="font-outfit font-extrabold text-xl">{reels.length}</div>
          <div className="text-[10px] text-white/60 uppercase tracking-wide">Reels</div>
        </div>
      </div>

      {/* Streamer stats card */}
      {(user.total_streams ?? 0) > 0 && (
        <div className="mb-4 p-3 rounded-3xl bg-gradient-to-br from-[#FF2A5F]/10 to-[#00E5FF]/10 border border-white/10" data-testid="streamer-stats">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-outfit font-bold text-sm flex items-center gap-1"><Trophy className="w-4 h-4 text-[#FF7A00]"/>Streamer Stats</h3>
            <span className="text-[10px] text-white/60">XP: {user.stream_xp ?? 0}</span>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center text-xs">
            <div><div className="font-bold">{user.total_streams}</div><div className="text-[9px] text-white/60 uppercase">Streams</div></div>
            <div><div className="font-bold">{user.total_stream_minutes}m</div><div className="text-[9px] text-white/60 uppercase">Time</div></div>
            <div><div className="font-bold">{user.total_stream_viewers}</div><div className="text-[9px] text-white/60 uppercase">Viewers</div></div>
          </div>
        </div>
      )}

      {isOwn && (
        <>
          <Link to="/wallet" data-testid="profile-wallet" className="block mb-3 p-4 rounded-3xl bg-gradient-to-r from-[#FF7A00]/20 to-[#FF2A5F]/15 border border-[#FF7A00]/30 flex items-center justify-between active:scale-[0.99] transition">
            <div>
              <div className="text-xs text-white/60 uppercase tracking-wider">My Wallet</div>
              <div className="flex items-center gap-3 mt-1">
                <span className="flex items-center gap-1 text-[#FF7A00] font-bold"><Coins className="w-4 h-4"/>{user.wallet_goldly}</span>
                <span className="flex items-center gap-1 text-[#FF2A5F] font-bold"><Diamond className="w-4 h-4"/>{user.wallet_tokwin}</span>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-white/40"/>
          </Link>

          <div className="grid grid-cols-2 gap-2 mb-5">
            <Link to="/boost" data-testid="profile-boost" className="p-4 rounded-3xl bg-white/5 border border-white/10 active:scale-[0.99]">
              <Zap className="w-5 h-5 text-[#00E5FF] mb-1"/>
              <div className="font-bold text-sm">Boost Reel</div>
              <div className="text-[10px] text-white/60">More views, followers</div>
            </Link>
            <Link to="/live" data-testid="profile-live" className="p-4 rounded-3xl bg-white/5 border border-white/10 active:scale-[0.99]">
              <Radio className="w-5 h-5 text-[#FF2A5F] mb-1"/>
              <div className="font-bold text-sm">Go Live</div>
              <div className="text-[10px] text-white/60">Earn XP & badges</div>
            </Link>
            <Link to="/my-gifts" data-testid="profile-mygifts" className="p-4 rounded-3xl bg-white/5 border border-white/10 active:scale-[0.99]">
              <Award className="w-5 h-5 text-[#FF7A00] mb-1"/>
              <div className="font-bold text-sm">My Gifts</div>
              <div className="text-[10px] text-white/60">Gifting history</div>
            </Link>
            <Link to="/ai-studio" data-testid="profile-aistudio" className="p-4 rounded-3xl bg-gradient-to-br from-[#00E5FF]/15 to-[#A855F7]/10 border border-[#00E5FF]/30 active:scale-[0.99]">
              <Wand2 className="w-5 h-5 text-[#00E5FF] mb-1"/>
              <div className="font-bold text-sm">AI Studio <span className="text-[8px] uppercase font-extrabold text-[#FF2A5F] ml-0.5">New</span></div>
              <div className="text-[10px] text-white/60">Generate images · Goldly</div>
            </Link>
          </div>

          {/* Monetization Banner */}
          <div className="mb-5 rounded-3xl p-4 bg-gradient-to-br from-[#00E5FF]/10 via-[#FF2A5F]/8 to-[#FF7A00]/8 border border-[#00E5FF]/30 relative overflow-hidden" data-testid="monetization-banner">
            <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-[#FF2A5F]/90 text-[10px] font-bold uppercase">Phase 2</div>
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5 text-[#00E5FF]"/>
              <h3 className="font-outfit font-extrabold text-lg">💰 Monetization Available</h3>
            </div>
            <p className="text-xs text-white/70 mb-3">Earn from your reels! Meet these criteria to qualify when monetization activates:</p>
            <div className="space-y-1.5">
              {CRITERIA.map(c => (
                <div key={c.label} className="flex items-center gap-2 text-xs">
                  <c.icon className="w-3.5 h-3.5 text-[#00E5FF]"/>
                  <span className="text-white/80">{c.label}</span>
                </div>
              ))}
            </div>
            <div className="mt-3 text-[10px] text-[#FF7A00] font-bold uppercase tracking-wide">Coming in Phase 2 • Activation Soon</div>
          </div>
        </>
      )}

      {reels.length > 0 && (
        <>
          <h2 className="font-outfit font-bold text-xl mb-2">Reels</h2>
          <div className="grid grid-cols-3 gap-1 mb-5">
            {reels.map((r, idx) => (
              <button key={r.id} onClick={() => setPlayingReelIndex(idx)}
                className="aspect-[9/16] rounded-lg overflow-hidden bg-black/50 relative group active:scale-95 transition"
                data-testid={`my-reel-${r.id}`}>
                <video src={r.video_url} className="w-full h-full object-cover" muted preload="metadata"
                  onLoadedMetadata={(e) => { try { e.currentTarget.currentTime = 0.1; } catch (_e) { /* noop */ } }}/>
                {/* Play overlay so it's visually obvious it's a video */}
                <div className="absolute inset-0 flex items-center justify-center bg-black/10">
                  <div className="w-9 h-9 rounded-full bg-black/55 backdrop-blur flex items-center justify-center">
                    <Play className="w-4 h-4 text-white fill-white"/>
                  </div>
                </div>
                <div className="absolute bottom-1 left-1 right-1 text-[10px] flex items-center gap-1 text-white/90 drop-shadow">
                  <Heart className="w-3 h-3"/>{r.likes}
                  <Eye className="w-3 h-3 ml-1"/>{r.views}
                </div>
                {r.boosted && <div className="absolute top-1 right-1 px-1 py-0.5 rounded bg-[#FF2A5F] text-[8px] font-bold">BOOST</div>}
              </button>
            ))}
          </div>
        </>
      )}

      {/* Inline reel player modal — full TikTok-style scrollable feed for this profile */}
      {playingReelIndex !== null && (
        <ProfileReelPlayer
          reels={reels}
          startIndex={playingReelIndex}
          onClose={() => setPlayingReelIndex(null)}
        />
      )}

      {/* Top Gifters of this profile */}
      {topGifters.length > 0 && (
        <div className="mb-5" data-testid="top-gifters">
          <h2 className="font-outfit font-bold text-xl mb-2 flex items-center gap-2"><Trophy className="w-5 h-5 text-[#FF7A00]"/>Top Gifters</h2>
          <p className="text-xs text-white/60 mb-2">Biggest supporters of @{user.username || user.name || "user"}</p>
          <div className="space-y-1.5">
            {topGifters.slice(0, 10).map(g => (
              <Link key={g.user.id} to={`/u/${g.user.id}`} data-testid={`tg-${g.rank}`}
                className="flex items-center gap-2.5 p-2.5 rounded-2xl bg-white/5 border border-white/10 active:scale-[0.99]">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${g.rank<=3?"bg-gradient-to-br from-[#FF7A00] to-[#FF2A5F] text-black":"bg-white/10"}`}>
                  {g.rank<=3 ? (g.rank===1?"🥇":g.rank===2?"🥈":"🥉") : `#${g.rank}`}
                </div>
                <img src={g.user.avatar} className="w-8 h-8 rounded-full"/>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1 text-sm">
                    <span className="font-bold truncate">@{g.user.username}</span>
                    {g.user.verified && <BadgeCheck className="w-3.5 h-3.5 text-[#00E5FF]"/>}
                  </div>
                  <div className="text-[10px] text-white/60">{g.gifts_count} gifts</div>
                </div>
                <div className="flex items-center gap-1 text-[#FF7A00] font-bold text-sm"><Coins className="w-3.5 h-3.5"/>{g.total_amount.toLocaleString()}</div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {showShare && <ShareSheet
        url={`${window.location.origin}/u/${user.id}`}
        title={`Check out @${user.username || user.name || "user"} on Hifireels — ${user.bio || "Awesome creator!"}`}
        onClose={()=>setShowShare(false)}
      />}
    </div>
  );
}
